// src/components/SdkVersionsPanel.jsx
//
// SdkVersionsPanel — "Observed SDK/environment versions" readout.
//
// Lifted intact from the retired TimelinePage.jsx (the per-slot auction
// waterfall it lived on was superseded by AdLifecycleView.jsx and removed
// wholesale) because this scan has no other home: it's the only place in the
// tool that surfaces which SDK/env build tagged the *current* live traffic.
// Scans every row's URL for the first observed value of a fixed set of
// SDK/environment version params — first value wins, so it reads as "what
// this session is running," not a histogram of every build seen.
//
// `rows` is the flat row list to scan (pass liveTrafficGroups flattened, the
// same shape TimelinePage used) — this component does no fetching of its own.

const VERSION_PARAMS = ['js', 'jsv', 'sdkv', 'sdk', 'osv', 'av', 'lver', 'dtsdk', 'msid', 'an'];

function paramsOf(url) {
  try {
    const qIdx = url.indexOf('?');
    if (qIdx === -1) return null;
    return new URLSearchParams(url.slice(qIdx + 1));
  } catch {
    return null;
  }
}

/** Scans all rows for the first observed value of each VERSION_PARAMS key. */
function scanSdkVersions(rows) {
  const found = {};
  for (const r of rows) {
    try {
      const params = paramsOf(r?.url || '');
      if (!params) continue;
      for (const key of VERSION_PARAMS) {
        if (found[key] != null) continue;
        const v = params.get(key);
        if (v) found[key] = v;
      }
    } catch {
      // skip malformed row
    }
    if (Object.keys(found).length === VERSION_PARAMS.length) break;
  }
  return found;
}

export default function SdkVersionsPanel({ rows }) {
  const sdkVersions = scanSdkVersions(Array.isArray(rows) ? rows : []);
  const sdkVersionEntries = Object.entries(sdkVersions);

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
        Observed SDK/environment versions
      </div>
      {sdkVersionEntries.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          No version parameters observed yet.
        </div>
      ) : (
        <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
          <tbody>
            {sdkVersionEntries.map(([k, v]) => (
              <tr key={k}>
                <td style={{ padding: '3px 14px 3px 0', color: 'var(--muted)', fontFamily: 'monospace' }}>{k}</td>
                <td style={{ padding: '3px 0', fontFamily: 'monospace', color: 'var(--ink)' }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, fontStyle: 'italic' }}>
        Live view only — this is a point-in-time radar of the SDK/env versions seen in current
        traffic. Build-over-build version tracking already exists in the qaRunner regression
        triage; this panel does not replace it.
      </div>
    </div>
  );
}
